import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isIntercomEnabled, showIntercomMessenger } from '@/lib/intercom';
import { cn } from '@/lib/utils';
import fundsHistoryIcon from '@/assets/settings/funds-history.png';
import inviteFriendsIcon from '@/assets/settings/invite-friends.png';
import supportIcon from '@/assets/settings/support.png';
import loyaltyIcon from '@/assets/settings/loyalty.png';

/** Shared shell for one settings shortcut tile (Figma "Widget"). */
function WidgetTile({
  icon,
  label,
  testId,
  onClick,
  disabled,
  children,
  dimIcon,
}: {
  icon: string;
  label: string;
  testId: string;
  onClick?: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  dimIcon?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`settings-widget-${testId}`}
      className={cn(
        'relative flex h-[77px] items-center gap-1 overflow-hidden rounded-[14px] border-[1.5px] border-stroke-soft bg-surface pl-1 text-left',
        disabled ? 'cursor-default' : 'transition-opacity hover:opacity-85 active:opacity-75',
      )}
    >
      <img src={icon} alt="" className={cn('size-[69px] shrink-0 object-contain', dimIcon && 'opacity-30')} />
      <span className={cn('text-lg font-medium capitalize leading-tight', disabled ? 'text-muted' : 'text-ink')}>
        {label}
      </span>
      {children}
    </button>
  );
}

/** Rotated "Coming Soon" corner ribbon (Loyalty tile). */
function ComingSoonRibbon() {
  return (
    <span
      aria-hidden="true"
      className="absolute -left-8 top-3 w-32 -rotate-[36deg] bg-purple py-1 text-center text-[10px] font-semibold leading-none text-white"
    >
      Coming Soon
    </span>
  );
}

/**
 * 2×2 grid of settings shortcuts (Figma "Widgets"): Funds History → wallet,
 * Invite Friends → native share (clipboard fallback), Support → Intercom
 * messenger (hidden when Intercom is not configured), Loyalty → coming soon.
 */
export function SettingsWidgets() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  async function handleInvite() {
    const url = window.location.origin;
    if (navigator.share) {
      // Cancelling the share sheet rejects with AbortError — not an error state.
      await navigator.share({ title: 'Crown Prediction', url }).catch(() => undefined);
      return;
    }
    // clipboard is absent in insecure contexts / some webviews — feature-check
    // before touching it, and only claim "copied" when the write succeeded.
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return;
    }
    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <WidgetTile icon={fundsHistoryIcon} label="Funds History" testId="funds-history" onClick={() => navigate('/wallet')} />
      <WidgetTile
        icon={inviteFriendsIcon}
        label={copied ? 'Link Copied!' : 'Invite Friends'}
        testId="invite-friends"
        onClick={() => void handleInvite()}
      />
      {isIntercomEnabled() && (
        <WidgetTile icon={supportIcon} label="Support" testId="support" onClick={showIntercomMessenger} />
      )}
      <WidgetTile icon={loyaltyIcon} label="Loyalty" testId="loyalty" disabled dimIcon>
        <ComingSoonRibbon />
      </WidgetTile>
    </div>
  );
}
